import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import api from '../../utils/functions/api';
import { showAlert } from '../../utils/functions/globalDialog';

const DAY_MS = 24 * 60 * 60 * 1000;

// ถังขยะโครงการ — โครงการที่ลบจากคอลัมน์จัดการถูกเก็บไว้ 1 ปี (backend: TRASH_RETENTION_DAYS) แล้วลบจริงอัตโนมัติ
// กดกู้คืนเพื่อนำกลับมาในตารางโครงการ
export default function ProjectTrashDialog({ open, onClose, onRestored }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [restoringId, setRestoringId] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`${process.env.REACT_APP_API_URL}/project/trash`);
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('project trash load error', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const handleRestore = async (row) => {
    setRestoringId(row.id);
    try {
      await api.post(`${process.env.REACT_APP_API_URL}/project/${row.id}/restore`);
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      showAlert(`กู้คืนโครงการ ${row.project_number || row.name} แล้ว`);
      if (onRestored) onRestored();
    } catch (e) {
      console.error('project restore error', e);
    } finally {
      setRestoringId('');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>ถังขยะโครงการ</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          โครงการที่ลบจะถูกเก็บไว้ที่นี่ 1 ปี แล้วลบจริงอัตโนมัติ ระหว่างนั้นกดกู้คืนได้
          (ข้อมูลที่ผูกกับโครงการ เช่น ค่าใช้จ่ายและการลงเวลา ไม่ถูกลบ)
        </Typography>
        {loading ? (
          <CircularProgress size={28} sx={{ display: 'block', mx: 'auto', my: 4 }} />
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>เลขโครงการ</TableCell>
                <TableCell>ชื่อโครงการ</TableCell>
                <TableCell>ลบเมื่อ</TableCell>
                <TableCell>ลบโดย</TableCell>
                <TableCell>ลบจริงอีก</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    ถังขยะว่างเปล่า
                  </TableCell>
                </TableRow>
              )}
              {rows.map((row) => {
                const daysLeft = Math.max(0, Math.ceil((new Date(row.purge_at).getTime() - Date.now()) / DAY_MS));

                return (
                  <TableRow key={row.id} hover>
                    <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{row.project_number}</TableCell>
                    <TableCell>{row.name}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{dayjs(row.deleted_at).format('DD/MM/YYYY HH:mm')}</TableCell>
                    <TableCell>{row.deleted_by_name || '-'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{`${daysLeft} วัน`}</TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={restoringId === row.id}
                        onClick={() => handleRestore(row)}
                      >
                        กู้คืน
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>ปิด</Button>
      </DialogActions>
    </Dialog>
  );
}
